import * as React from 'react';
import { cn } from '@/lib/utils';

export interface CDEditableTextareaProps {
  /** The text value to display */
  value: string | null | undefined;
  /** Whether this is an editable field */
  editable?: boolean;
  /** onChange handler for editable mode */
  onChange?: (value: string) => void;
  /** Placeholder text for empty values */
  placeholder?: string;
  /** Size variant */
  size?: 'tiny' | 'small' | 'body';
  /** Whether the text should be bold */
  bold?: boolean;
  /** Number of visible rows (for editable mode) */
  rows?: number;
  /** Additional className */
  className?: string;
}

/**
 * CDEditableTextarea - Editable multi-line text field component
 *
 * Displays multi-line text (like addresses) that can be edited when in editable mode.
 * Matches CD form styling seamlessly.
 */
export const CDEditableTextarea = React.forwardRef<HTMLSpanElement, CDEditableTextareaProps>(
  (
    {
      value,
      editable = false,
      onChange,
      placeholder = '',
      size = 'tiny',
      bold = false,
      rows = 2,
      className,
    },
    ref,
  ) => {
    const displayValue = value || placeholder;

    const sizeClass = {
      tiny: 'cd-text-tiny',
      small: 'cd-text-small',
      body: 'cd-text-body',
    }[size];

    if (editable) {
      return (
        <textarea
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className={cn(
            'cd-input cd-input-textarea w-full resize-none',
            sizeClass,
            bold && 'font-bold',
            className,
          )}
        />
      );
    }

    return (
      <span
        ref={ref}
        className={cn(
          sizeClass,
          'whitespace-pre-line',
          bold && 'font-bold',
          !value && 'text-cd-gray-text',
          className,
        )}
      >
        {displayValue}
      </span>
    );
  },
);

CDEditableTextarea.displayName = 'CDEditableTextarea';
